import { createFileRoute, Link } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import type { MouseEvent } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { SANDBOX_TOOLS } from '~/components/routes/sandbox-previsions/sandbox-tools'
import { Button } from '~/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '~/components/ui/card'
import { useFeatureFlagEvaluator } from '~/hooks/useFeatureFlag'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/sandbox-previsions/')({
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: SandboxPrevisionsPage,
})

withRoutePermissions(Route)

const GuideButton = ({ href }: { href: string }) => {
  const openGuide = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <Button variant="outline" size="sm" onClick={openGuide}>
      <Download />
      Guide PDF
    </Button>
  )
}

function SandboxPrevisionsPage() {
  const isFlagEnabled = useFeatureFlagEvaluator()
  const tools = SANDBOX_TOOLS.filter((tool) => isFlagEnabled(tool.flagKey))

  return (
    <>
      <AppHeader>
        <Title>Sandbox prévisions</Title>
      </AppHeader>
      <AppContainer>
        {tools.length === 0 && <p className="text-muted-foreground">Aucun outil activé.</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {tools.map((tool) => (
            <Link key={tool.to} to={tool.to} className="group/tool rounded-4xl outline-none">
              <Card className="h-full transition-colors hover:bg-muted group-focus-visible/tool:ring-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <tool.icon className="size-5" />
                    {tool.title}
                  </CardTitle>
                  <CardDescription>{tool.description}</CardDescription>
                </CardHeader>
                {tool.guideHref && (
                  <CardFooter>
                    <GuideButton href={tool.guideHref} />
                  </CardFooter>
                )}
              </Card>
            </Link>
          ))}
        </div>
      </AppContainer>
    </>
  )
}
